import type { PrismCatalogQuery } from '@lib/input/prism-import.input'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { prismImportService } from '@/services/PrismImportService'
import type {
  PrismImportJobDto,
  PrismImportJobListDto,
  PrismCatalogDto,
  PrismCatalogScanDto,
  PrismSessionDto,
  PrismWipePreviewDto,
  PrismWipeResultDto,
} from '@lib/dto/prism-import.dto'
import type {
  SavePrismSessionInput,
  StartPrismImportInput,
  WipeTenantDataInput,
} from '@lib/input/prism-import.input'

export const prismImportKeys = {
  all: ['prism-import'] as const,
  lists: () => [...prismImportKeys.all, 'list'] as const,
  list: () => [...prismImportKeys.lists()] as const,
  details: () => [...prismImportKeys.all, 'detail'] as const,
  detail: (id: string) => [...prismImportKeys.details(), id] as const,
  session: () => [...prismImportKeys.all, 'session'] as const,
  wipe: () => [...prismImportKeys.all, 'wipe'] as const,
  /** Every page of the catalog, for invalidating after a change. */
  catalogs: () => [...prismImportKeys.all, 'catalog'] as const,
  catalog: (query?: object, page?: number) =>
    [...prismImportKeys.catalogs(), query ?? 'all', page ?? 'all'] as const,
}

/** Recent Prism import jobs for this tenant. Polls while any job is active. */
export function usePrismImportJobs(): UseQueryResult<PrismImportJobListDto> {
  return useQuery({
    queryKey: prismImportKeys.list(),
    queryFn: () => prismImportService.list(),
    refetchInterval: (q) => {
      const jobs = q.state.data?.jobs ?? []
      const active = jobs.some((j) => j.status === 'queued' || j.status === 'running')
      return active ? 2000 : false
    },
  })
}

/** One job with live log; polls while queued/running. */
export function usePrismImportJob(id: string): UseQueryResult<PrismImportJobDto> {
  return useQuery({
    queryKey: prismImportKeys.detail(id),
    queryFn: () => prismImportService.get(id),
    enabled: id.length > 0,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'queued' || s === 'running' ? 1500 : false
    },
  })
}

/** The kept catalog of Social Tables events and where each import stands. */
export function usePrismCatalog(
  importRunning = false,
  query: PrismCatalogQuery = {},
  page?: number,
): UseQueryResult<PrismCatalogDto> {
  return useQuery({
    queryKey: prismImportKeys.catalog(query, page),
    queryFn: () => prismImportService.catalog(query, page),
    // The page's rows stay while the next page loads, so paging a catalog
    // somebody is working down does not blank the table under them.
    placeholderData: (previous) => previous,
    // Whether an event is imported is settled by a job that runs after the button
    // is clicked, so while one is in flight the answer on screen is out of date.
    // Polling for that stretch is what makes a row flip to Imported on its own
    // instead of waiting for someone to reload the page.
    refetchInterval: importRunning ? 3000 : false,
  })
}

/**
 * Crawl Social Tables and append what it lists to the catalog.
 *
 * The refreshed catalog it returns used to be written straight into the query,
 * to save a round trip after a crawl that already takes a while. It cannot be
 * now: the catalog is keyed by page and filter, and what a scan returns is the
 * unpaged, unfiltered list — writing that into a paged key would replace one
 * page with everything. So every page is invalidated instead, and the one on
 * screen refetches.
 */
export function useScanPrismCatalog(): UseMutationResult<PrismCatalogScanDto, Error, void> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => prismImportService.scanCatalog(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prismImportKeys.catalogs() })
    },
  })
}

export function useSetPrismCatalogVerified(): UseMutationResult<
  PrismCatalogDto,
  Error,
  { catalogId: string; verified: boolean }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ catalogId, verified }: { catalogId: string; verified: boolean }) =>
      prismImportService.setCatalogVerified(catalogId, verified),
    onSuccess: () => {
      // Invalidated rather than written in, for the same reason as the scan.
      void qc.invalidateQueries({ queryKey: prismImportKeys.catalogs() })
    },
  })
}

/**
 * Import one catalog event, or throw its import away and do it again.
 *
 * Both queue an ordinary import job, so the caller opens the same log it would
 * for a bulk run. The catalog is invalidated rather than written: whether the
 * event is imported is only settled once that job has finished.
 */
export function useImportCatalogEvent(): UseMutationResult<
  PrismImportJobDto,
  Error,
  { catalogId: string; reimport: boolean }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ catalogId, reimport }: { catalogId: string; reimport: boolean }) =>
      reimport
        ? prismImportService.reimportCatalogEvent(catalogId)
        : prismImportService.importCatalogEvent(catalogId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prismImportKeys.catalog() })
      void qc.invalidateQueries({ queryKey: prismImportKeys.lists() })
    },
  })
}

export function useStartPrismImport(): UseMutationResult<PrismImportJobDto, Error, StartPrismImportInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: StartPrismImportInput) => prismImportService.start(input),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: prismImportKeys.lists() })
      qc.setQueryData(prismImportKeys.detail(job.id), job)
    },
  })
}

/** Stop a queued or running job. */
export function useCancelPrismImport(): UseMutationResult<PrismImportJobDto, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => prismImportService.cancel(id),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: prismImportKeys.lists() })
      qc.setQueryData(prismImportKeys.detail(job.id), job)
    },
  })
}

/** What is known about the stored Social Tables session. */
export function usePrismSession(): UseQueryResult<PrismSessionDto> {
  return useQuery({
    queryKey: prismImportKeys.session(),
    queryFn: () => prismImportService.session(),
  })
}

/** Replace the stored session with one minted locally. */
export function useSavePrismSession(): UseMutationResult<PrismSessionDto, Error, SavePrismSessionInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SavePrismSessionInput) => prismImportService.saveSession(input),
    onSuccess: (session) => qc.setQueryData(prismImportKeys.session(), session),
  })
}

/** Forget the stored session. */
export function useClearPrismSession(): UseMutationResult<PrismSessionDto, Error, void> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => prismImportService.clearSession(),
    onSuccess: (session) => qc.setQueryData(prismImportKeys.session(), session),
  })
}

/** How much data a reset would clear. Read while the confirmation is open. */
export function usePrismWipePreview(enabled: boolean): UseQueryResult<PrismWipePreviewDto> {
  return useQuery({
    queryKey: prismImportKeys.wipe(),
    queryFn: () => prismImportService.wipePreview(),
    enabled,
    // The counts are the warning, so they are read fresh every time it is opened.
    staleTime: 0,
  })
}

/**
 * Clear the organization's imported data. Everything cached describes what was
 * just removed, so the whole cache goes with it.
 */
export function usePrismWipe(): UseMutationResult<PrismWipeResultDto, Error, WipeTenantDataInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: WipeTenantDataInput) => prismImportService.wipe(input),
    onSuccess: () => {
      qc.clear()
    },
  })
}
